/**
 * Land Assessment — eight weighted lenses, scoring, history, hydrate.
 * Persisted per scenario version on the deal (`landAssessment.byScenarioId`).
 */

export const LAND_ASSESSMENT_LENSES = [
  {
    id: 'location',
    name: 'Location & surroundings',
    shortLabel: 'Location',
    weight: 0.15,
    question: 'Is this somewhere people genuinely want to live?',
    consider: 'Neighbourhood quality, amenities, transport, schools, noise, stigma, micro-location within the commune.',
    redFlags: 'Declining area, heavy nuisances, isolation from amenities, or a location buyers systematically avoid.',
  },
  {
    id: 'market',
    name: 'Market & product fit',
    shortLabel: 'Market',
    weight: 0.10,
    question: 'What can realistically be sold here, to whom, and how quickly?',
    consider: 'Buyer segments, absorption, competing stock, price bands, product typology fit, sales velocity.',
    redFlags: 'Oversupply, mismatched product, thin buyer pool, or prices that only work on paper.',
  },
  {
    id: 'planning',
    name: 'Planning & authorisation',
    shortLabel: 'Planning',
    weight: 0.20,
    question: 'What can legally be built, and how likely is approval?',
    consider: 'Zoning, density, footprint, height, setbacks, heritage restrictions, municipal plans, political environment, planning precedents and neighbour opposition.',
    redFlags: 'Rezoning dependency, hostile municipality, exceptional authorisations, strong neighbour opposition or theoretical rights that authorities historically refuse.',
    critical: true,
  },
  {
    id: 'site',
    name: 'Site & environment',
    shortLabel: 'Site',
    weight: 0.10,
    question: 'What physical or environmental conditions constrain the project?',
    consider: 'Topography, soil, flood, contamination, trees, access, utilities, easements, geotech risk.',
    redFlags: 'Flood zone, contamination, inaccessible site, or environmental constraints that erase the buildable envelope.',
    critical: true,
  },
  {
    id: 'technical',
    name: 'Technical access & buildability',
    shortLabel: 'Buildability',
    weight: 0.10,
    question: 'Can the permitted volume be built efficiently?',
    consider: 'Structure, parking solution, construction method, phasing, access, cost premiums from site constraints.',
    redFlags: 'Unbuildable basement, uneconomic parking, blocked access, or construction complexity that destroys margin.',
    critical: true,
  },
  {
    id: 'legal',
    name: 'Legal & ownership',
    shortLabel: 'Legal',
    weight: 0.10,
    question: 'Can the land be controlled and developed cleanly?',
    consider: 'Title, co-ownership, options, conditions precedent, easements, litigation, seller reliability.',
    redFlags: 'Unclear title, blocked co-owners, fragile options, or legal defects that prevent clean closing.',
    critical: true,
  },
  {
    id: 'price',
    name: 'Price & economics',
    shortLabel: 'Price',
    weight: 0.20,
    question: 'Does the price leave sufficient value after costs and risks?',
    consider: 'Land basis, residual value, soft costs, contingency, financing, target margin and sensitivity.',
    redFlags: 'Price assumes best-case planning, no contingency, or residual value that only works at peak GDV.',
  },
  {
    id: 'execution',
    name: 'Execution & timing',
    shortLabel: 'Execution',
    weight: 0.05,
    question: 'Can the opportunity be delivered within a reasonable timeframe?',
    consider: 'Planning timeline, permits, construction duration, sales pace, team capacity, capital lock-up.',
    redFlags: 'Multi-year authorisation risk, no delivery path, or timing that breaks the financing plan.',
  },
];

/** Lenses where score 1 is a fatal land flag (auto NO GO unless overridden). */
export const FATAL_LENS_IDS = ['planning', 'legal', 'site', 'technical'];

/** Below this confidence, final verdict is capped at WATCH. */
export const LAND_CONFIDENCE_WATCH_CAP = 70;

export const SCORE_META = {
  1: { label: 'Critical problem', color: 'bg-red-600 text-white border-red-600' },
  2: { label: 'Material weakness', color: 'bg-orange-500 text-white border-orange-500' },
  3: { label: 'Workable or uncertain', color: 'bg-gray-500 text-white border-gray-500' },
  4: { label: 'Strong', color: 'bg-emerald-400 text-white border-emerald-400' },
  5: { label: 'Exceptional advantage', color: 'bg-emerald-700 text-white border-emerald-700' },
};

const LAND_POTENTIAL_IDS = ['location', 'planning', 'site', 'technical', 'legal'];
const DEAL_POTENTIAL_IDS = ['market', 'price', 'execution', 'planning', 'legal'];

function emptyScores() {
  return Object.fromEntries(LAND_ASSESSMENT_LENSES.map((l) => [l.id, null]));
}
function emptyNotes() {
  return Object.fromEntries(LAND_ASSESSMENT_LENSES.map((l) => [l.id, '']));
}
function emptyEvidence() {
  return Object.fromEntries(LAND_ASSESSMENT_LENSES.map((l) => [l.id, []]));
}

export function notesPlaceholder(score) {
  if (score == null) return 'Add context for this lens…';
  if (score <= 2) return 'Explain the risk, restriction or uncertainty…';
  if (score === 3) return 'Explain what remains uncertain or average…';
  return 'Explain the advantage and supporting evidence…';
}

export function createEmptyAssessment(scenarioId = null) {
  return {
    scenarioId: scenarioId || null,
    scores: emptyScores(),
    notes: emptyNotes(),
    evidence: emptyEvidence(),
    interpretation: null,
    history: [],
    fatalOverrides: {},
    updatedAt: null,
  };
}

export function hydrateAssessment(raw, scenarioId = null) {
  const base = createEmptyAssessment(scenarioId);
  if (!raw || typeof raw !== 'object') return base;
  const scores = { ...base.scores };
  const notes = { ...base.notes };
  const evidence = { ...base.evidence };
  for (const lens of LAND_ASSESSMENT_LENSES) {
    const s = raw.scores?.[lens.id];
    scores[lens.id] = (s === 1 || s === 2 || s === 3 || s === 4 || s === 5) ? s : null;
    notes[lens.id] = typeof raw.notes?.[lens.id] === 'string' ? raw.notes[lens.id] : '';
    const ev = raw.evidence?.[lens.id];
    evidence[lens.id] = Array.isArray(ev)
      ? ev.filter((e) => e && typeof e === 'object').map((e) => ({
        id: typeof e.id === 'string' ? e.id : `ev-${lens.id}-${Math.random().toString(36).slice(2, 8)}`,
        label: typeof e.label === 'string' ? e.label : (e.fileName || e.url || 'Evidence'),
        url: typeof e.url === 'string' ? e.url : '',
        documentId: typeof e.documentId === 'string' ? e.documentId : null,
        fileName: typeof e.fileName === 'string' ? e.fileName : null,
      }))
      : [];
  }
  return {
    scenarioId: typeof raw.scenarioId === 'string' ? raw.scenarioId : scenarioId,
    scores,
    notes,
    evidence,
    interpretation: raw.interpretation && typeof raw.interpretation === 'object' ? raw.interpretation : null,
    history: Array.isArray(raw.history) ? raw.history.slice(0, 30) : [],
    fatalOverrides: hydrateFatalOverrides(raw.fatalOverrides),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : null,
  };
}

function hydrateFatalOverrides(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [lensId, value] of Object.entries(raw)) {
    if (!FATAL_LENS_IDS.includes(lensId) || !value || typeof value !== 'object') continue;
    const justification = typeof value.justification === 'string' ? value.justification.trim() : '';
    if (justification.length < 10) continue;
    out[lensId] = {
      justification,
      overriddenAt: typeof value.overriddenAt === 'string' ? value.overriddenAt : null,
    };
  }
  return out;
}

export function setFatalOverride(assessment, lensId, justification) {
  const a = hydrateAssessment(assessment);
  if (!FATAL_LENS_IDS.includes(lensId)) return a;
  const text = typeof justification === 'string' ? justification.trim() : '';
  const fatalOverrides = { ...(a.fatalOverrides || {}) };
  if (!text) {
    delete fatalOverrides[lensId];
  } else {
    fatalOverrides[lensId] = {
      justification: text,
      overriddenAt: new Date().toISOString(),
    };
  }
  return hydrateAssessment({ ...a, fatalOverrides }, a.scenarioId);
}

export function clearFatalOverride(assessment, lensId) {
  return setFatalOverride(assessment, lensId, '');
}

export function listFatalFlags(assessment) {
  const a = hydrateAssessment(assessment);
  const flags = [];
  for (const lens of LAND_ASSESSMENT_LENSES) {
    if (!FATAL_LENS_IDS.includes(lens.id)) continue;
    if (a.scores[lens.id] !== 1) continue;
    const override = a.fatalOverrides?.[lens.id] || null;
    flags.push({
      lensId: lens.id,
      name: lens.name,
      message: `${lens.name} scored 1 — fatal land flag`,
      overridden: Boolean(override),
      justification: override?.justification || '',
      overriddenAt: override?.overriddenAt || null,
    });
  }
  return flags;
}

export function activeFatalFlags(assessment) {
  return listFatalFlags(assessment).filter((f) => !f.overridden);
}

/**
 * Combine financial verdict with Land Assessment.
 * Land score is NOT averaged into the ROI % — it only shapes the final GO/WATCH/NO GO.
 */
/**
 * Session helper: quality-grade financial verdict + land assessment → final verdict.
 * Keeps App / share / Deal Quality on one merge path.
 */
export function buildCombinedVerdict({
  qualityGrade = {},
  decision = {},
  assessment = null,
} = {}) {
  const financialVerdict = qualityGrade.effectiveVerdict || decision.verdict;
  const financialReason = qualityGrade.effectiveReason || decision.reason || '';
  const financialHardFail = Boolean(
    qualityGrade.inputsInvalid
    || qualityGrade.overall === 'FAIL'
    || financialVerdict === 'NO GO',
  );
  return mergeVerdictWithLand({
    financialVerdict,
    financialReason,
    financialHardFail,
    assessment,
  });
}

export function mergeVerdictWithLand({
  financialVerdict,
  financialReason = '',
  financialHardFail = false,
  assessment = null,
} = {}) {
  const summary = computeAssessmentSummary(assessment);
  const landScore = summary.overallScore;
  const fatals = listFatalFlags(assessment);
  const activeFatals = fatals.filter((f) => !f.overridden);
  const incomplete = summary.scoredCount < LAND_ASSESSMENT_LENSES.length;
  const lowConfidence = summary.confidence < LAND_CONFIDENCE_WATCH_CAP;
  const financial = normalizeVerdict(financialVerdict);

  const base = {
    landScore,
    landCompletionLabel: summary.completionLabel,
    landConfidence: summary.confidence,
    incomplete,
    lowConfidence,
    fatalFlags: fatals,
    activeFatalFlags: activeFatals,
    financialVerdict: financial,
    financialReason: financialReason || '',
  };

  if (financialHardFail || financial === 'NO GO') {
    return {
      ...base,
      verdict: 'NO GO',
      reason: financialReason || 'Financial assessment failed a hard rule',
      landDriven: false,
    };
  }

  if (activeFatals.length > 0) {
    return {
      ...base,
      verdict: 'NO GO',
      reason: `Fatal land flag: ${activeFatals[0].name} (score 1)`,
      landDriven: true,
    };
  }

  if (landScore != null && landScore < 50) {
    return {
      ...base,
      verdict: 'NO GO',
      reason: `Land score ${landScore} is below 50`,
      landDriven: true,
    };
  }

  let verdict;
  let reason;
  let landDriven = false;

  if (landScore != null && landScore >= 70) {
    verdict = 'GO';
    reason = `Financial pass + land score ${landScore}`;
    landDriven = true;
  } else if (landScore != null && landScore >= 50) {
    verdict = 'WATCH';
    reason = `Land score ${landScore} is in the watch band (50–69)`;
    landDriven = true;
  } else {
    // No land score yet — start from financial, then apply incomplete cap
    verdict = financial === 'GO' || financial === 'WATCH' ? financial : 'WATCH';
    reason = financialReason || 'Land assessment not scored yet';
    landDriven = false;
  }

  if ((incomplete || lowConfidence) && verdict === 'GO') {
    verdict = 'WATCH';
    reason = incomplete
      ? `Land assessment incomplete (${summary.completionLabel}) — capped at WATCH`
      : `Land confidence ${summary.confidence}% — capped at WATCH`;
    landDriven = true;
  }

  return { ...base, verdict, reason, landDriven };
}

function normalizeVerdict(value) {
  const v = String(value || '').toUpperCase().trim();
  if (v === 'GO' || v === 'WATCH' || v === 'NO GO') return v;
  if (v === 'NOGO' || v === 'NO-GO') return 'NO GO';
  return 'WATCH';
}


export function hydrateLandAssessmentStore(raw) {
  if (!raw || typeof raw !== 'object') return { byScenarioId: {} };
  if (raw.scores && !raw.byScenarioId) {
    const id = raw.scenarioId || '_default';
    return { byScenarioId: { [id]: hydrateAssessment(raw, id) } };
  }
  const byScenarioId = {};
  const src = raw.byScenarioId && typeof raw.byScenarioId === 'object' ? raw.byScenarioId : {};
  for (const [id, value] of Object.entries(src)) {
    byScenarioId[id] = hydrateAssessment(value, id);
  }
  return { byScenarioId };
}

export function getAssessmentForScenario(store, scenarioId) {
  const hydrated = hydrateLandAssessmentStore(store);
  const id = scenarioId || '_default';
  return hydrateAssessment(hydrated.byScenarioId[id], id);
}

export function upsertAssessmentForScenario(store, scenarioId, assessment) {
  const hydrated = hydrateLandAssessmentStore(store);
  const id = scenarioId || '_default';
  return {
    byScenarioId: {
      ...hydrated.byScenarioId,
      [id]: hydrateAssessment({ ...assessment, scenarioId: id, updatedAt: new Date().toISOString() }, id),
    },
  };
}

export function scoredCount(assessment) {
  const a = hydrateAssessment(assessment);
  return LAND_ASSESSMENT_LENSES.filter((l) => a.scores[l.id] != null).length;
}

function weightedSubset(assessment, ids) {
  const a = hydrateAssessment(assessment);
  let usedWeight = 0;
  let usedScore = 0;
  let missing = 0;
  for (const lens of LAND_ASSESSMENT_LENSES) {
    if (!ids.includes(lens.id)) continue;
    const score = a.scores[lens.id];
    if (score == null) {
      missing += 1;
      continue;
    }
    usedWeight += lens.weight;
    usedScore += (score / 5) * lens.weight;
  }
  if (usedWeight <= 0) return { score: null, missing };
  return { score: Math.round((usedScore / usedWeight) * 100), missing };
}

export function computeAssessmentSummary(assessment) {
  const a = hydrateAssessment(assessment);
  const unscored = [];
  const criticalFlags = [];
  let weighted = 0;
  let usedWeight = 0;

  for (const lens of LAND_ASSESSMENT_LENSES) {
    const score = a.scores[lens.id];
    if (score == null) {
      unscored.push(lens.id);
      continue;
    }
    weighted += (score / 5) * lens.weight;
    usedWeight += lens.weight;
    if (lens.critical && score === 1) {
      criticalFlags.push({
        lensId: lens.id,
        name: lens.name,
        message: `${lens.name} scored 1 — fatal land flag`,
      });
    }
  }

  const overall = usedWeight > 0 ? Math.round((weighted / usedWeight) * 100) : null;
  const fullWeighted = unscored.length === 0
    ? Math.round(LAND_ASSESSMENT_LENSES.reduce((sum, lens) => sum + ((a.scores[lens.id] / 5) * lens.weight), 0) * 100)
    : overall;

  const land = weightedSubset(a, LAND_POTENTIAL_IDS);
  const deal = weightedSubset(a, DEAL_POTENTIAL_IDS);
  const scored = LAND_ASSESSMENT_LENSES.length - unscored.length;

  return {
    overallScore: fullWeighted,
    verdictLabel: verdictForScore(fullWeighted, criticalFlags.length > 0),
    landPotential: land.score,
    dealPotential: deal.score,
    confidence: Math.round((scored / LAND_ASSESSMENT_LENSES.length) * 100),
    unscoredCount: unscored.length,
    unscoredIds: unscored,
    scoredCount: scored,
    criticalFlags,
    completionLabel: `${scored} of ${LAND_ASSESSMENT_LENSES.length} lenses scored`,
  };
}

export function verdictForScore(score, hasCritical = false) {
  if (score == null) return 'Score lenses to see potential';
  if (hasCritical) return 'Critical issue — do not rely on the headline score';
  if (score < 40) return 'Weak opportunity — likely pass';
  if (score < 60) return 'Challenging — material issues to resolve';
  if (score < 75) return 'Promising, further investigation required';
  if (score < 90) return 'Strong opportunity';
  return 'Exceptional opportunity';
}

export function radarData(assessment) {
  const a = hydrateAssessment(assessment);
  return LAND_ASSESSMENT_LENSES.map((lens) => ({
    id: lens.id,
    subject: lens.shortLabel,
    fullName: lens.name,
    score: a.scores[lens.id],
    value: a.scores[lens.id] ?? 0,
    fullMark: 5,
  }));
}

export function snapshotHistoryEntry(assessment) {
  const a = hydrateAssessment(assessment);
  const summary = computeAssessmentSummary(a);
  return {
    id: `hist-${Date.now().toString(36)}`,
    savedAt: new Date().toISOString(),
    scores: { ...a.scores },
    notes: { ...a.notes },
    fatalOverrides: { ...(a.fatalOverrides || {}) },
    overallScore: summary.overallScore,
    verdictLabel: summary.verdictLabel,
    criticalCount: summary.criticalFlags.length,
  };
}

export function pushHistory(assessment, max = 20) {
  const a = hydrateAssessment(assessment);
  const entry = snapshotHistoryEntry(a);
  return {
    ...a,
    history: [entry, ...(a.history || [])].slice(0, max),
    updatedAt: entry.savedAt,
  };
}

export function resetAssessment(scenarioId) {
  return createEmptyAssessment(scenarioId);
}

export function restoreFromHistory(assessment, historyId) {
  const a = hydrateAssessment(assessment);
  const entry = (a.history || []).find((h) => h.id === historyId);
  if (!entry) return a;
  return hydrateAssessment({
    ...a,
    scores: entry.scores,
    notes: entry.notes || a.notes,
    // Older history entries omit fatalOverrides — keep current overrides in that case.
    fatalOverrides: entry.fatalOverrides !== undefined ? entry.fatalOverrides : a.fatalOverrides,
    interpretation: null,
    updatedAt: new Date().toISOString(),
  }, a.scenarioId);
}

export function buildInterpretationRequest(assessment, meta = {}) {
  const a = hydrateAssessment(assessment);
  const summary = computeAssessmentSummary(a);
  return {
    projectName: meta.projectName || null,
    scenarioName: meta.scenarioName || null,
    address: meta.address || null,
    overallScore: summary.overallScore,
    verdictLabel: summary.verdictLabel,
    criticalFlags: summary.criticalFlags,
    unscoredIds: summary.unscoredIds,
    landPotential: summary.landPotential,
    dealPotential: summary.dealPotential,
    confidence: summary.confidence,
    lenses: LAND_ASSESSMENT_LENSES.map((lens) => ({
      id: lens.id,
      name: lens.name,
      weight: lens.weight,
      question: lens.question,
      score: a.scores[lens.id],
      notes: a.notes[lens.id] || '',
      evidence: (a.evidence[lens.id] || []).map((e) => e.label || e.url || e.fileName).filter(Boolean),
    })),
  };
}
